import React, { useState, useEffect } from 'react';
import { useLocation, } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { TextField, Grid, Button, Typography, FormControlLabel, Switch, MenuItem, Box, CircularProgress, InputAdornment } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import InputMask from 'react-input-mask';

import TituloPagina from '../../components/layout/TituloPagina';
import If from '../../components/condicional/if';
import { getRepasseAoVendedor, autorizarRepasseAoVendedor, atualizarRepasse } from '../../store/actions/pagamentos';
import LoadingOverlay from '../../components/layout/LoadingOverlay';
import DialogAlert from '../../components/layout/DialogAlert';
import moedaBr from '../../assets/js/moedaBr';

// 1. Crie ou ajuste o componente que faz a ponte com o Material UI
const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator="."     // Opcional: Define o ponto para os milhares
            decimalSeparator=","      // Define a vírgula como separador decimal

        />
    );
});

const DetalheRepasse = props => {
    const {
        repasseAoVendedor,
        getRepasseAoVendedor,
        autorizarRepasseAoVendedor,
        atualizarRepasse,
        loading,
        processing,
        user
    } = props;
    const { perfis } = user;
    const location = useLocation();
    const [disableFields, setDisableFields] = useState(true);
    const [processingMessage, setProcessingMessage] = useState('');
    const [toggleDialog, setToggleDialog] = useState(false);
    const [toggleConfirmDialog, setToggleConfirmDialog] = useState(false);
    const [pendingSubmitValues, setPendingSubmitValues] = useState(null);
    const [mensagemErro, setMensagemErro] = useState('');
    const isPlatformAdmin = Array.isArray(perfis) && perfis.includes('ADMIN_APP');

    useEffect(() => {
        const id = Number(location?.state?.repasseId);

        if (id > 0) {
            getRepasseAoVendedor(id);
        }
    }, [location, getRepasseAoVendedor]);

    const parseDateBR = (value) => {
        if (!value) {
            return null;
        }

        const normalizedValue = String(value).trim();
        const match = normalizedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

        if (!match) {
            return null;
        }

        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);
        const parsedDate = new Date(year, month - 1, day);

        const isValidDate = parsedDate.getFullYear() === year
            && parsedDate.getMonth() === month - 1
            && parsedDate.getDate() === day;

        return isValidDate ? parsedDate : null;
    };

    const requiredMoneyField = (label) => yup
        .number()
        .transform((value, originalValue) => {
            if (originalValue === '' || originalValue === null || originalValue === undefined) {
                return undefined;
            }

            return value;
        })
        .typeError(`O campo ${label} é obrigatório`)
        .required(`O campo ${label} é obrigatório`);

    const validationSchema = yup.object({
        valorLiquidoRepasse: requiredMoneyField('Valor Líquido do Repasse'),
        valorComissaoPlataforma: requiredMoneyField('Valor da Comissão da Plataforma'),
        valorTaxaGatewayProRata: requiredMoneyField('Valor da Taxa do Gateway Pro Rata'),
        dataPrevistaLiberacao: yup
            .string()
            .required('A data prevista de liberação é obrigatória')
            .test(
                'data-prevista-valida',
                'Informe uma data prevista de liberação válida',
                (value) => Boolean(parseDateBR(value))
            ),
    });

    const formik = useFormik({
        initialValues: {
            id: repasseAoVendedor?.id || '',
            pedidoId: repasseAoVendedor?.pedidoId || '',
            sellerStripeAccountId: repasseAoVendedor?.sellerStripeAccountId || '',
            nomeVendedor: repasseAoVendedor?.nomeVendedor || '',
            stripeChargeId: repasseAoVendedor?.stripeChargeId || '',
            valorTotalPedido: repasseAoVendedor?.valorTotalPedido || 0,
            valorProdutos: repasseAoVendedor?.valorProdutos || 0,
            valorFrete: repasseAoVendedor?.valorFrete || 0,
            valorComissaoPlataforma: repasseAoVendedor?.valorComissaoPlataforma || 0,
            valorLiquidoRepasse: repasseAoVendedor?.valorLiquidoRepasse || 0,
            valorTaxaGatewayProRata: repasseAoVendedor?.valorTaxaGatewayProRata || 0,
            status: repasseAoVendedor?.status || 0,
            dataCriacao: repasseAoVendedor?.dataCriacao || '',
            dataPrevistaLiberacao: repasseAoVendedor?.dataPrevistaLiberacao || '',
            dataEfetivaTransferencia: repasseAoVendedor?.dataEfetivaTransferencia || '',
            stripeTransferId: repasseAoVendedor?.stripeTransferId || '',
            observacoes: repasseAoVendedor?.observacoes || ''
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if ((isPlatformAdmin && Object.keys(repasseAoVendedor).length === 0)) {
                setMensagemErro('Você precisa identificar o repasse.');
                setToggleDialog(true);
                return;
            }

            setPendingSubmitValues(values);
            setToggleConfirmDialog(true);
        },
        enableReinitialize: true,
    });

    const handleDialogError = () => {
        setToggleDialog(false);
        setMensagemErro('');
    };

    const handleAutorizarRepasse = () => {
        if ((isPlatformAdmin && Object.keys(repasseAoVendedor).length === 0)) {
            setMensagemErro('Você precisa identificar o repasse.');
            setToggleDialog(true);
            return;
        }
        autorizarRepasseAoVendedor(repasseAoVendedor.id);
    };

    const handleConfirmSubmit = () => {
        if (!pendingSubmitValues) {
            setToggleConfirmDialog(false);
            return;
        }

        setToggleConfirmDialog(false);
        setProcessingMessage('Salvando...');

        atualizarRepasse(pendingSubmitValues);

        setPendingSubmitValues(null);
        setProcessingMessage('');
        setDisableFields(true);
    };

    const handleCancelSubmit = () => {
        setToggleConfirmDialog(false);
        setPendingSubmitValues(null);
        formik.resetForm();
        setDisableFields(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <>
            <TituloPagina titulo='Detalhe do Repasse' subtitulo={new Date().toLocaleString('pt-BR')} />
            <Grid container item xs={12}>
                <form onSubmit={formik.handleSubmit} noValidate autoComplete='off' style={{ width: 'inherit' }}>
                    <div style={{ flexGrow: 1, paddingTop: '15px' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id='id'
                                    name='id'
                                    label='ID do Repasse'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.id}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id='pedidoId'
                                    name='pedidoId'
                                    label='ID do Pedido'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.pedidoId}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    id='status'
                                    name='status'
                                    label='Status do Repasse'
                                    variant='filled'
                                    value={formik.values.status}
                                    disabled={disableFields}
                                    onChange={formik.handleChange}>
                                    <MenuItem key='1' value={1}>Aguardando Pagamento Cliente</MenuItem>
                                    <MenuItem key='2' value={2}>Aguardando Prazo de Entrega</MenuItem>
                                    <MenuItem key='3' value={3}>Transferência solicitada</MenuItem>
                                    <MenuItem key='4' value={4}>Transferência realizada</MenuItem>
                                    <MenuItem key='5' value={5}>Estornado</MenuItem>
                                    <MenuItem key='6' value={6}>Bloqueado Disputa</MenuItem>

                                </TextField>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} >
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    fullWidth
                                    id='nomeVendedor'
                                    name='nomeVendedor'
                                    label='Vendedor'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.nomeVendedor}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} >
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='sellerStripeAccountId'
                                    name='sellerStripeAccountId'
                                    label='Conta Stripe do Vendedor'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.sellerStripeAccountId}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='stripeChargeId'
                                    name='stripeChargeId'
                                    label='ID da Cobrança Stripe'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.stripeChargeId}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='stripeTransferId'
                                    name='stripeTransferId'
                                    label='ID da Transferência Stripe'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.stripeTransferId}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} >
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='valorProdutos'
                                    name='valorProdutos'
                                    label='Valor dos Produtos do Vendedor'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.valorProdutos}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='valorFrete'
                                    name='valorFrete'
                                    label='Valor do Frete'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.valorFrete}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='valorTotalPedido'
                                    name='valorTotalPedido'
                                    label='Valor Total do Pedido'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.valorTotalPedido}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} >
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='valorLiquidoRepasse'
                                    name='valorLiquidoRepasse'
                                    label='Valor Líquido do Repasse'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.valorLiquidoRepasse}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='valorComissaoPlataforma'
                                    name='valorComissaoPlataforma'
                                    label='Valor da Comissão da Plataforma'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.valorComissaoPlataforma}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='valorTaxaGatewayProRata'
                                    name='valorTaxaGatewayProRata'
                                    label='Valor da Taxa do Gateway Pro Rata'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.valorTaxaGatewayProRata}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} >
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='dataCriacao'
                                    name='dataCriacao'
                                    label='Data de Criação'
                                    variant='filled'
                                    disabled={true}
                                    value={formik.values.dataCriacao}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={'99/99/9999'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.dataPrevistaLiberacao}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='dataPrevistaLiberacao'
                                        name='dataPrevistaLiberacao'
                                        label='Data Prevista de Liberação'
                                        variant='filled'
                                        error={formik.touched.dataPrevistaLiberacao && Boolean(formik.errors.dataPrevistaLiberacao)}
                                        helperText={formik.touched.dataPrevistaLiberacao && formik.errors.dataPrevistaLiberacao}
                                    />}
                                </InputMask>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={'99/99/9999'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.dataEfetivaTransferencia}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='dataEfetivaTransferencia'
                                        name='dataEfetivaTransferencia'
                                        label='Data Efetiva de Transferência'
                                        variant='filled'
                                        error={formik.touched.dataEfetivaTransferencia && Boolean(formik.errors.dataEfetivaTransferencia)}
                                        helperText={formik.touched.dataEfetivaTransferencia && formik.errors.dataEfetivaTransferencia}
                                    />}
                                </InputMask>
                            </Grid>
                        </Grid>


                        <Grid container pt={4} style={{ paddingTop: '25px', paddingBottom: '25px' }}>
                            <If test={isPlatformAdmin && disableFields && repasseAoVendedor?.status === 2}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button variant='contained' color='primary' onClick={handleAutorizarRepasse}>Autorizar Repasse</Button>
                                </Grid>
                            </If>
                            <If test={isPlatformAdmin && disableFields && repasseAoVendedor?.status !== 4}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button type='submit' color='secondary' variant='contained' onClick={() => setDisableFields(false)}>Editar</Button>
                                </Grid>
                            </If>
                            <If test={isPlatformAdmin && !disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button type='submit' color='secondary' variant='contained'>Salvar</Button>
                                </Grid>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button variant='contained' href='/repasses-ao-vendedor'>Cancelar</Button>
                                </Grid>
                            </If>
                            <If test={disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button color='secondary' variant='contained' href='/repasses-ao-vendedor'>Voltar</Button>
                                </Grid>
                            </If>
                        </Grid>
                    </div>
                </form>
            </Grid>

            {/* O componente Loading fica ouvindo o estado isProcessing */}
            <LoadingOverlay
                open={processing}
                message={processingMessage}
            />

            <DialogAlert
                title='Confirmação'
                okButton={true}
                labelOkButton='Sim'
                okButtonClick={handleConfirmSubmit}
                cancelButton={true}
                labelCancelButton='Não'
                cancelButtonClick={handleCancelSubmit}
                openDialog={toggleConfirmDialog}>
                Deseja realmente salvar as alterações realizadas?
            </DialogAlert>

            <DialogAlert
                title='Atenção'
                okButton={true}
                labelOkButton='Ok'
                okButtonClick={handleDialogError}
                openDialog={toggleDialog}>
                {mensagemErro}
            </DialogAlert>
        </>
    )
}

const mapStateToProps = state => ({
    repasseAoVendedor: state.pagamentos.repasseAoVendedor,
    processing: state.pagamentos.processing,
    loading: state.pagamentos.loading,
    user: state.auth.user,
});

const mapDispatchToProps = dispatch => bindActionCreators({
    getRepasseAoVendedor,
    autorizarRepasseAoVendedor,
    atualizarRepasse,
}, dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(DetalheRepasse);